import { Capacitor } from '@capacitor/core';

/**
 * Pendo product analytics integration.
 *
 * Flow:
 *   1. initPendo() runs on app boot, loads the agent, and starts an anonymous
 *      session so unauth pages still get page views.
 *   2. identifyPendo() upgrades the anonymous visitor to the real player once
 *      the Descope session resolves and Redux populates playerGUID.
 *
 * The visitor id is the engine `playerGUID` (EN-10466) — the same value the
 * engine keys a player on, so a Pendo visitor maps 1:1 to a player row and
 * stays stable across devices, browsers and reinstalls.
 */

// A Pendo API key is a public, client-side identifier — it ships in the bundle
// by design and is not a secret, which is why it is inline rather than an env var.
const PENDO_API_KEY = '62d6f330-7aca-44f0-a7c1-759a4fa55cf2';

let initialized = false;

/**
 * Stages that are real players. An ALLOWLIST, deliberately — the denylist this
 * replaced (`staging` / `test` / `test1`) was the trap documented on SfxLab's
 * gate (components/SfxLab/index.tsx):
 *
 *   1. Production deploys under TWO names, `production` AND `live`
 *      (deploy-web-app.yml:172), so any gate written against one name alone is
 *      wrong for the other.
 *   2. deploy-web-app.yml offers eleven non-production stages — develop,
 *      develop2, develop3, test, test2..test6, staging. The old denylist named
 *      three of them, and `test1` is not even a deployable stage, so nine lower
 *      environments were sending live traffic into the production subscription.
 *   3. Defaulting to enabled means every stage invented after this line is
 *      written silently starts polluting production analytics.
 *
 * Failing closed costs a missing dashboard until a stage is added here. Failing
 * open costs internal traffic mixed into production data, which cannot be
 * cleanly separated afterwards.
 */
const PRODUCTION_STAGES = ['production', 'live'];

export function isEnabled(): boolean {
  // `react-scripts build` always sets NODE_ENV=production, so this is a local
  // dev server or a jest run — never a deploy.
  if (process.env.NODE_ENV !== 'production') return false;

  // Skip local dev hosts (no env file needed locally).
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host.endsWith('.local')) return false;
  }

  const stage = (process.env.REACT_APP_ENVIRONMENT ?? '').toLowerCase();
  return PRODUCTION_STAGES.includes(stage);
}

/** Load the Pendo agent script — adapted from Pendo's official install snippet. */
function loadAgent(apiKey: string) {
  (function (p: any, e: any, n: any, d: any) {
    let w: any;
    let x: any;
    const o: any = (p[d] = p[d] || {});
    o._q = o._q || [];
    const v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent'];
    for (w = 0, x = v.length; w < x; ++w) {
      (function (m) {
        o[m] =
          o[m] ||
          function () {
            // eslint-disable-next-line prefer-rest-params
            o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
          };
      })(v[w]);
    }
    const y = e.createElement(n);
    y.async = !0;
    y.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
    const z = e.getElementsByTagName(n)[0];
    z.parentNode.insertBefore(y, z);
  })(window, document, 'script', 'pendo');
}

/** Initialize Pendo. Call once on app boot. */
export function initPendo(): void {
  if (initialized || !isEnabled()) return;
  initialized = true;

  loadAgent(PENDO_API_KEY);

  // Start anonymous — identifyPendo() upgrades this visitor once the player
  // logs in. No `visitor.id`/`account.id`: omitting them lets Pendo generate
  // its own stable per-browser anonymous id. The literal placeholders this
  // replaced ('VISITOR-UNIQUE-ID' / 'ACCOUNT-UNIQUE-ID') came straight from
  // Pendo's copy-paste snippet and were never substituted, so EVERY logged-out
  // browser shared one visitor id and collapsed into a single row — guest
  // lobby traffic was one "user" with an implausible session count, and any
  // pre-login funnel measured against it was meaningless (EN-10466).
  window.pendo?.initialize({
    visitor: {},
    account: {}
  });

  suppressGuidesWhenReady();
}

export interface PendoIdentity {
  playerGUID: string;
  displayName?: string;
  platform?: string;
  /** HARDWARE: the installed native shell version. Absent on web. */
  appVersion?: string | null;
  /** HARDWARE: native build number (Android versionCode / iOS CFBundleVersion). */
  appBuild?: string | null;
  /** SOFTWARE: the JS bundle actually running, which OTA moves independently. */
  bundleVersion?: string;
}

/**
 * The last visitor payload sent to Pendo, serialized.
 *
 * identify() is not free — it flushes and re-evaluates the agent — and the hook
 * that drives it re-runs on every user-slice change, so identical repeat calls
 * are dropped here. Keyed on the WHOLE payload rather than the GUID alone:
 * `displayName` hydrates AFTER the first identify for a player, and a
 * GUID-only guard would suppress that enriched call for the life of the page,
 * leaving the visitor permanently nameless in Pendo.
 */
let lastIdentity: string | null = null;
let identifiedPlayerGUID: string | null = null;

export interface PendoTrackEvent {
  playerGUID: string;
  /** snake_case, matching the names agreed with the data owner (Cory). */
  eventName: string;
  properties?: Record<string, string | number | boolean>;
  /** Client-session key: an event whose key was already accepted is not sent again. */
  dedupeKey?: string;
}

// Events waiting for their player's identify. Capped so a session that never
// identifies cannot grow it; the oldest go first.
const MAX_PENDING_TRACK_EVENTS = 50;
// Dedupe keys accepted this session. Vote reports add a few per hand, so the
// set is capped too, evicting the oldest key.
const MAX_TRACKED_EVENT_KEYS = 1000;
const IDENTIFY_RETRY_INITIAL_DELAY_MS = 250;
const IDENTIFY_RETRY_MAX_DELAY_MS = 5000;

let pendingTrackEvents: PendoTrackEvent[] = [];
const trackedEventKeys = new Set<string>();
let pendingIdentity: PendoIdentity | null = null;
let identifyRetryTimer: ReturnType<typeof setTimeout> | null = null;
let identifyRetryDelayMs = IDENTIFY_RETRY_INITIAL_DELAY_MS;

const rememberEventKey = (key: string): void => {
  if (trackedEventKeys.size >= MAX_TRACKED_EVENT_KEYS) {
    const oldest = trackedEventKeys.values().next().value;
    if (oldest !== undefined) trackedEventKeys.delete(oldest);
  }
  trackedEventKeys.add(key);
};

const clearIdentifyRetry = (): void => {
  if (identifyRetryTimer !== null) clearTimeout(identifyRetryTimer);
  identifyRetryTimer = null;
  identifyRetryDelayMs = IDENTIFY_RETRY_INITIAL_DELAY_MS;
};

/**
 * The one retry this service runs. Otherwise identify is re-attempted only by
 * a later user-slice change, and once the slice has hydrated nothing promises
 * another, so events queued for the player would wait indefinitely.
 */
const scheduleIdentifyRetry = (): void => {
  if (identifyRetryTimer !== null || !pendingIdentity) return;
  identifyRetryTimer = setTimeout(() => {
    identifyRetryTimer = null;
    if (pendingIdentity) identifyPendo(pendingIdentity);
  }, identifyRetryDelayMs);
  identifyRetryDelayMs = Math.min(identifyRetryDelayMs * 2, IDENTIFY_RETRY_MAX_DELAY_MS);
};

/**
 * Stamped on every event. Pendo keeps only a visitor's LATEST platform and
 * version, so the event is the only record of the build a player was on when
 * it happened. No environment: the agent only ever runs in production.
 */
const sharedEventProperties = () => ({
  platform: Capacitor.getPlatform(),
  // Same source as utils/version's BUNDLE_VERSION, read here directly so the
  // analytics service does not load the native @capacitor/app plugin.
  bundle_version: process.env.REACT_APP_VERSION || 'dev'
});

/**
 * Best effort and not retried: the install stub provides track() and queues it
 * until the agent loads, so a throw here is not a state worth recovering from.
 */
const sendTrackEvent = ({ eventName, properties }: PendoTrackEvent): void => {
  try {
    window.pendo?.track(eventName, { ...sharedEventProperties(), ...properties });
  } catch {
    // Analytics is a convenience, never a dependency.
  }
};

const flushTrackEventsForVisitor = (playerGUID: string): void => {
  // Events created for another visitor must never survive an identity switch
  // and later be attributed to the wrong Pendo session.
  const matching = pendingTrackEvents.filter((event) => event.playerGUID === playerGUID);
  pendingTrackEvents = [];
  matching.forEach(sendTrackEvent);
};

/**
 * Record a client event for one authenticated player. Returns false when the
 * event is refused (Pendo disabled, no GUID); true once it is sent, queued, or
 * already accepted under its dedupe key.
 *
 * LaunchDarkly can resolve before Pendo's asynchronous identity enrichment.
 * Holding the event against the expected GUID guarantees it is never attached
 * to the anonymous visitor or to the player who previously used the device.
 */
export function trackPendoEvent(event: PendoTrackEvent): boolean {
  if (!isEnabled() || !event.playerGUID || !event.eventName) return false;
  if (event.dedupeKey) {
    if (trackedEventKeys.has(event.dedupeKey)) return true;
    rememberEventKey(event.dedupeKey);
  }

  if (identifiedPlayerGUID === event.playerGUID) {
    sendTrackEvent(event);
  } else {
    pendingTrackEvents.push(event);
    if (pendingTrackEvents.length > MAX_PENDING_TRACK_EVENTS) pendingTrackEvents.shift();
  }
  return true;
}

/**
 * Upgrade the anonymous session to an identified player. Safe to call repeatedly.
 *
 * `playerGUID` is the engine's player identifier (EN-10466): it is what the
 * engine keys a player on, it is what the game client already persists and
 * resubscribes with, and it is stable across devices and sessions — so one
 * Pendo visitor is one player, which is the property the ticket asks to confirm.
 */
export function identifyPendo(identity: PendoIdentity): boolean {
  if (!isEnabled()) return false;

  const { playerGUID, displayName, platform, appVersion, appBuild, bundleVersion } = identity;

  // Never identify with an absent or placeholder id. A falsy GUID would make
  // Pendo mint a visitor keyed on the literal string and merge unrelated
  // players into it — the same class of bug as the init-time placeholder above.
  if (!playerGUID || playerGUID === 'VISITOR-UNIQUE-ID') return false;

  const visitor = {
    id: playerGUID,
    ...(displayName ? { displayName } : {}),
    ...(platform ? { platform } : {}),
    // Both versions, never one merged number: appVersion is the shell the
    // player installed, bundleVersion is the JS it is running, and they
    // diverge as soon as an OTA update lands. Segmenting on the wrong one is
    // how you conclude a fix shipped when it did not.
    //
    // These are part of the fingerprint below on purpose. The native read is
    // async, so it hydrates AFTER the first identify for a session, exactly
    // like displayName, and that second call has to be sent rather than
    // deduped away or the visitor stays on a bundle-only record all session.
    ...(appVersion ? { appVersion } : {}),
    ...(appBuild ? { appBuild } : {}),
    ...(bundleVersion ? { bundleVersion } : {})
  };

  // Re-send when ANY field changed (a new player, or metadata that has just
  // hydrated); skip only an exact repeat.
  const fingerprint = JSON.stringify(visitor);
  if (lastIdentity === fingerprint) return true;

  // Nothing to identify against yet — the agent stub is installed
  // synchronously by loadAgent(), so this means init never ran or was blocked
  // (CSP, ad blocker, an initPendo() throw). Leave the guard UNSET and retry,
  // as for a throwing agent below; marking it sent here would suppress every
  // retry and the player would go unidentified for the life of the page.
  const agent = window.pendo;
  pendingIdentity = identity;
  if (!agent?.identify) {
    scheduleIdentifyRetry();
    return false;
  }

  try {
    agent.identify({ visitor });
    // Only now is this payload actually delivered.
    pendingIdentity = null;
    clearIdentifyRetry();
    lastIdentity = fingerprint;
    identifiedPlayerGUID = playerGUID;
    flushTrackEventsForVisitor(playerGUID);
    return true;
  } catch {
    scheduleIdentifyRetry();
    return false;
  }
}

/**
 * Return Pendo to an anonymous visitor at a session-end boundary (logout or a
 * direct account switch).
 *
 * Ask the AGENT to end the identified session, and drop everything bound to
 * the old player: the pending identify and its retry, queued events, dedupe
 * keys, and the guard, so the next identify() is sent even if the payload is
 * unchanged. Clearing only the local guard would leave the agent still
 * reporting the previous player, so post-logout page views would be
 * attributed to them.
 *
 * `clearSession` exists only on the fully-loaded agent. Before the CDN script
 * arrives, `window.pendo` is the install snippet's stub, which queues calls in
 * `_q` for the real agent to replay on load — so an identify or track sent
 * moments earlier is still pending, and the agent would replay it for the
 * player we just signed out. Drop those queued calls in that case. A throwing
 * agent is not retried: the next identify replaces the visitor regardless.
 */
export function resetPendoIdentity(): void {
  clearIdentifyRetry();
  pendingIdentity = null;
  pendingTrackEvents = [];
  trackedEventKeys.clear();
  lastIdentity = null;
  identifiedPlayerGUID = null;
  try {
    const agent = window.pendo as (PendoAgent & { _q?: unknown[] }) | undefined;
    if (agent?.clearSession) {
      agent.clearSession();
    } else if (agent && Array.isArray(agent._q)) {
      agent._q = agent._q.filter(
        (entry) => !['identify', 'track'].includes(String((entry as unknown[])?.[0]))
      );
    }
  } catch {
    // Agent not ready; nothing it holds outlives the next identify.
  }
}

/** Test seam — clears the once-per-load guards. */
export function __resetPendoForTests(): void {
  clearIdentifyRetry();
  initialized = false;
  lastIdentity = null;
  identifiedPlayerGUID = null;
  pendingTrackEvents = [];
  trackedEventKeys.clear();
  pendingIdentity = null;
}

/**
 * PERF - no guides are published today (product, 2026-08-20), so the guide /
 * Resource Center engine is pure overhead: profiled on device rebuilding and
 * re-measuring Resource Center building blocks on EVERY DOM mutation - and a
 * live felt's animations mutate ~700 times/second - for ~15% of a core,
 * continuously, all session (investigation doc 8x/8y). stopGuides() measured
 * a 60% cut of Pendo's at-rest cost; analytics events are a separate
 * subsystem and are never affected. FLIP THIS TO TRUE when product publishes
 * a guide - the gesture gate in App.tsx then owns pausing around swipes.
 * The deeper fix is dashboard-side: the Resource Center configured for this
 * app churns without ever mounting - unpublish or fix its targeting.
 */
const GUIDES_IN_USE = false;

/**
 * PERF - pause Pendo's guide engine while a table-swipe gesture is in flight.
 *
 * The guide engine re-evaluates guide targeting by walking the DOM and
 * reading computed styles, in 30-80ms bursts - measured landing mid-swipe as
 * gesture jitter (investigation doc 8d/8p). Guides stay fully enabled on
 * tables otherwise (product plans in-table guides, e.g. "swipe to switch
 * tables"), and analytics events are never affected - only the guide
 * scanning pauses, and only while a finger or the settle spring owns the
 * table surface. While GUIDES_IN_USE is false the engine stays stopped
 * outright and re-enable requests are ignored.
 */
export function setPendoGuidesEnabled(enabled: boolean): void {
  const pendo = window.pendo as any;
  if (!pendo) return;
  try {
    if (enabled && GUIDES_IN_USE) pendo.startGuides?.();
    else if (!enabled || !GUIDES_IN_USE) pendo.stopGuides?.();
  } catch {
    /* pendo not ready yet - the next toggle will catch it */
  }
}

/**
 * Stop the guide engine as soon as the async agent has loaded. The stub
 * queue only proxies the init-time methods, so stopGuides can't be queued
 * before the agent arrives - poll briefly instead. Bounded: gives up after
 * ~30s (agent blocked / offline), where there is nothing to stop anyway.
 */
function suppressGuidesWhenReady(): void {
  if (GUIDES_IN_USE) return;
  let tries = 0;
  const id = window.setInterval(() => {
    tries += 1;
    const pendo = window.pendo as any;
    if (pendo?.stopGuides) {
      try {
        pendo.stopGuides();
      } catch {
        /* not ready - next tick */
        if (tries < 15) return;
      }
      window.clearInterval(id);
      return;
    }
    if (tries >= 15) window.clearInterval(id);
  }, 2000);
}
